/**
 * Admin Image Management Utilities
 * เครื่องมือสำหรับจัดการรูปภาพในระบบแอดมิน
 */

import { doc, getDoc, updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { getMaxImagesByTier } from './imageUtils';

/**
 * ตรวจสอบและแก้ไขปัญหาการสร้างรูปซ้ำ
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<{hasDuplicates: boolean, cleanedCount: number}>}
 */
export const checkAndFixDuplicateImages = async (girlId) => {
  try {
    const girlRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlRef);
    
    if (!girlDoc.exists()) {
      return { hasDuplicates: false, cleanedCount: 0 };
    }
    
    const girlData = girlDoc.data();
    const images = girlData.images || [];
    const pendingImages = girlData.pendingImages || [];
    const rejectedImages = girlData.rejectedImages || [];
    
    // หารูปซ้ำใน images
    const duplicateImages = [];
    const seenHashes = new Set();
    const seenUrls = new Set();
    
    images.forEach((image, index) => {
      const hash = image.fileHash || `${image.originalName}_${image.originalSize}`;
      const url = image.url;
      
      if (seenHashes.has(hash) || seenUrls.has(url)) {
        duplicateImages.push({ ...image, index, reason: 'duplicate' });
      } else {
        seenHashes.add(hash);
        seenUrls.add(url);
      }
    });
    
    // ลบรูปซ้ำออกจาก images
    const cleanedImages = images.filter((image, index) => 
      !duplicateImages.some(dup => dup.index === index)
    );
    
    // อัพเดทฐานข้อมูลถ้ามีรูปซ้ำ
    if (duplicateImages.length > 0) {
      await updateDoc(girlRef, {
        images: cleanedImages,
        lastUpdated: new Date().toISOString()
      });
      
      console.log(`Cleaned ${duplicateImages.length} duplicate images for girl ${girlId}`);
    }
    
    return {
      hasDuplicates: duplicateImages.length > 0,
      cleanedCount: duplicateImages.length
    };
  } catch (error) {
    console.error('Error checking and fixing duplicate images:', error);
    return { hasDuplicates: false, cleanedCount: 0 };
  }
};

/**
 * ตรวจสอบข้อมูล tier ของผู้ใช้
 * @param {string} girlId - ID ของผู้ใช้
 * @returns {Promise<{tier: string, maxImages: number, currentImages: number}>}
 */
export const checkUserTierInfo = async (girlId) => {
  try {
    // ดึงข้อมูลจาก girls collection
    const girlRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlRef);
    
    if (!girlDoc.exists()) {
      return { tier: 'free', maxImages: 1, currentImages: 0 };
    }
    
    const girlData = girlDoc.data();
    const currentImages = (girlData.images || []).length;
    
    // ดึงข้อมูล tier จาก users collection
    const userRef = doc(db, 'users', girlId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.exists() ? userDoc.data() : {};
    
    const tier = userData.tier || 'free';
    const maxImages = getMaxImagesByTier(tier, 'girl');
    
    return {
      tier,
      maxImages,
      currentImages,
      canApproveMore: maxImages === -1 || currentImages < maxImages
    };
  } catch (error) {
    console.error('Error checking user tier info:', error);
    return { tier: 'free', maxImages: 1, currentImages: 0, canApproveMore: false };
  }
};

/**
 * อนุมัติรูปภาพพร้อมตรวจสอบและแก้ไขปัญหา
 * @param {string} imageId - ID ของรูปภาพ
 * @param {string} girlId - ID ของผู้ใช้
 * @param {Array} pendingImages - รายการรูปรออนุมัติ
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const approveImageWithChecks = async (imageId, girlId, pendingImages) => {
  try {
    console.log('🔍 approveImageWithChecks started:');
    console.log('- Image ID:', imageId);
    console.log('- Girl ID:', girlId);
    console.log('- Pending Images Count:', pendingImages.length);
    
    // หารูปที่ต้องการอนุมัติ
    const imageToApprove = pendingImages.find(img => img.id === imageId);
    if (!imageToApprove) {
      console.log('❌ Image not found in pending images');
      return { success: false, message: 'ไม่พบรูปภาพที่ต้องการอนุมัติ' };
    }
    
    console.log('📷 Image to Approve Details:', {
      id: imageToApprove.id,
      fileName: imageToApprove.fileName,
      originalName: imageToApprove.originalName,
      url: imageToApprove.url,
      fileHash: imageToApprove.fileHash,
      originalSize: imageToApprove.originalSize,
      status: imageToApprove.status
    });
    
    // ตรวจสอบและแก้ไขรูปซ้ำ
    console.log('🧹 Checking and fixing duplicates...');
    const duplicateCheck = await checkAndFixDuplicateImages(girlId);
    if (duplicateCheck.hasDuplicates) {
      console.log(`✅ Found and cleaned ${duplicateCheck.cleanedCount} duplicate images`);
    } else {
      console.log('✅ No duplicates found');
    }
    
    // ตรวจสอบข้อมูล tier
    console.log('⭐ Checking tier info...');
    const tierInfo = await checkUserTierInfo(girlId);
    console.log('Tier Info:', tierInfo);
    
    if (!tierInfo.canApproveMore) {
      console.log('❌ Cannot approve more - tier limit reached');
      return { 
        success: false, 
        message: `ผู้ใช้นี้มีรูปภาพครบจำนวนสูงสุดแล้ว (${tierInfo.maxImages} รูป) ไม่สามารถอนุมัติเพิ่มได้` 
      };
    }
    
    // ดึงข้อมูลปัจจุบัน
    console.log('📊 Fetching current girl data...');
    const girlRef = doc(db, 'girls', girlId);
    const girlDoc = await getDoc(girlRef);
    const girlData = girlDoc.data();
    const existingImages = girlData.images || [];
    const currentPendingImages = girlData.pendingImages || [];
    
    console.log('📊 Current Data:', {
      existingImagesCount: existingImages.length,
      currentPendingImagesCount: currentPendingImages.length,
      existingImages: existingImages.map(img => ({
        id: img.id,
        fileName: img.fileName,
        originalName: img.originalName,
        url: img.url,
        fileHash: img.fileHash
      }))
    });
    
    // ตรวจสอบรูปซ้ำอีกครั้ง
    console.log('🔍 Checking for duplicates in existing images...');
    console.log('🔍 Existing images to check against:', existingImages.map(img => ({
      id: img.id,
      fileName: img.fileName,
      originalName: img.originalName,
      url: img.url,
      fileHash: img.fileHash,
      originalSize: img.originalSize
    })));
    
    console.log('🔍 Image to approve details:', {
      id: imageToApprove.id,
      fileName: imageToApprove.fileName,
      originalName: imageToApprove.originalName,
      url: imageToApprove.url,
      fileHash: imageToApprove.fileHash,
      originalSize: imageToApprove.originalSize
    });
    
    const isDuplicate = existingImages.some(existingImg => {
      const hashMatch = existingImg.fileHash === imageToApprove.fileHash;
      const urlMatch = existingImg.url === imageToApprove.url;
      const nameSizeMatch = existingImg.originalName === imageToApprove.originalName && 
                           existingImg.originalSize === imageToApprove.originalSize;
      
      console.log(`🔍 Checking against existing image ${existingImg.id}:`, {
        hashMatch,
        urlMatch,
        nameSizeMatch,
        isDuplicate: hashMatch || urlMatch || nameSizeMatch
      });
      
      return hashMatch || urlMatch || nameSizeMatch;
    });
    
    console.log('🔍 Final duplicate check result:', isDuplicate);
    
    if (isDuplicate) {
      console.log('❌ Duplicate found - cannot approve');
      return { success: false, message: 'รูปภาพนี้มีอยู่แล้วในระบบ กรุณาตรวจสอบอีกครั้ง' };
    }
    
    // อัพเดทสถานะรูป
    console.log('📝 Creating updated image object...');
    const updatedImage = {
      ...imageToApprove,
      status: 'approved',
      approvedAt: new Date().toISOString(),
      approvedBy: 'admin'
    };
    
    console.log('📝 Updated Image Object:', {
      id: updatedImage.id,
      fileName: updatedImage.fileName,
      status: updatedImage.status,
      approvedAt: updatedImage.approvedAt,
      approvedBy: updatedImage.approvedBy
    });
    
    // ย้ายรูปจาก pendingImages ไป images
    console.log('💾 Updating database...');
    console.log('- Removing from pendingImages:', imageToApprove.id);
    console.log('- Adding to images:', updatedImage.id);
    
    await updateDoc(girlRef, {
      pendingImages: arrayRemove(imageToApprove),
      images: arrayUnion(updatedImage),
      lastUpdated: new Date().toISOString()
    });
    
    console.log('✅ Database updated successfully');
    
    // ตรวจสอบผลลัพธ์
    console.log('🔍 Verifying update...');
    const updatedGirlDoc = await getDoc(girlRef);
    const updatedGirlData = updatedGirlDoc.data();
    const updatedImages = updatedGirlData.images || [];
    const updatedPendingImages = updatedGirlData.pendingImages || [];
    
    console.log('📊 Updated Data:', {
      imagesCount: updatedImages.length,
      pendingImagesCount: updatedPendingImages.length,
      images: updatedImages.map(img => ({
        id: img.id,
        fileName: img.fileName,
        status: img.status
      }))
    });
    
    return { success: true, message: 'อนุมัติรูปภาพเรียบร้อยแล้ว' };
  } catch (error) {
    console.error('💥 Error approving image:', error);
    return { success: false, message: 'เกิดข้อผิดพลาดในการอนุมัติ' };
  }
};

/**
 * ปฏิเสธรูปภาพ
 * @param {string} imageId - ID ของรูปภาพ
 * @param {string} girlId - ID ของผู้ใช้
 * @param {Array} pendingImages - รายการรูปรออนุมัติ
 * @param {string} reason - เหตุผลในการปฏิเสธ
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const rejectImageWithChecks = async (imageId, girlId, pendingImages, reason = '') => {
  try {
    // หารูปที่ต้องการปฏิเสธ
    const imageToReject = pendingImages.find(img => img.id === imageId);
    if (!imageToReject) {
      return { success: false, message: 'ไม่พบรูปภาพที่ต้องการปฏิเสธ' };
    }
    
    // อัพเดทสถานะรูป
    const updatedImage = {
      ...imageToReject,
      status: 'rejected',
      rejectedAt: new Date().toISOString(),
      rejectedBy: 'admin',
      rejectionReason: reason
    };
    
    // ย้ายรูปจาก pendingImages ไป rejectedImages
    const girlRef = doc(db, 'girls', girlId);
    await updateDoc(girlRef, {
      pendingImages: arrayRemove(imageToReject),
      rejectedImages: arrayUnion(updatedImage),
      lastUpdated: new Date().toISOString()
    });
    
    return { success: true, message: 'ปฏิเสธรูปภาพเรียบร้อยแล้ว' };
  } catch (error) {
    console.error('Error rejecting image:', error);
    return { success: false, message: 'เกิดข้อผิดพลาดในการปฏิเสธ' };
  }
};
